import React, { useState, useEffect } from 'react';
import { AppData, CountryRank } from '../types';
import { StoreBadge } from '../components/StoreBadge';
import { AppIcon } from '../components/AppIcon';
import { GrowthScoreGauge } from '../components/GrowthScoreGauge';
import { OutlierBadge } from '../components/OutlierBadge';
import { RankingChart } from '../components/RankingChart';
import { CountryRankings } from '../components/CountryRankings';
import { VersionHistorySection } from '../components/VersionHistorySection';
import { KeywordTable } from '../components/KeywordTable';
import { CompetitorTable } from '../components/CompetitorTable';
import { AIInsightsCard } from '../components/AIInsightsCard';
import { AddToCollectionModal } from '../components/AddToCollectionModal';
import { ShareModal } from '../components/ShareModal';
import { ProTeaserBanner } from '../components/ProTeaserBanner';
import { AppStoreTab } from '../components/tabs/AppStoreTab';
import { GooglePlayTab } from '../components/tabs/GooglePlayTab';
import { BuiltWithTab } from '../components/tabs/BuiltWithTab';
import { ActivityTab } from '../components/tabs/ActivityTab';
import { ReviewsTab } from '../components/tabs/ReviewsTab';
import { StorageService } from '../services/storage';
import { appCatalog } from '../services/appCatalogService';
import { useI18n } from '../services/i18n';
import { ProvenanceBadge, MetricSourceBadge } from '../components/MetricSourceBadge';
import { DataTrustEngine, TrustedMetric } from '../services/dataTrustEngine';
import { ClaimAppModal } from '../components/ClaimAppModal';
import { DeveloperAnalyticsDashboard } from '../components/DeveloperAnalyticsDashboard';
import {
  DeveloperAnalyticsService,
  VerifiedAnalyticsResponse,
  AppOwnership
} from '../services/developerAnalyticsService';
import {
  Check,
  Star,
  ExternalLink,
  Bookmark,
  Scale,
  FolderKanban,
  Share2,
  Globe,
  Loader2,
  Activity,
  Layers,
  Search,
  Key,
  ShieldCheck,
  TrendingUp,
  Cpu,
  MessageSquare,
  History,
  Lock,
  Sparkles,
  BarChart3,
  UserCheck
} from 'lucide-react';

interface AppProfileViewProps {
  appId: string;
  onNavigate: (route: string) => void;
  onSelectApp: (id: string) => void;
  onSelectDeveloper: (devSlug: string) => void;
  onCompare: (appIds: string[]) => void;
  onOpenSignIn?: () => void;
}

export type ProfileTab =
  | 'overview'
  | 'appstore'
  | 'googleplay'
  | 'rankings'
  | 'reviews'
  | 'keywords'
  | 'countries'
  | 'competitors'
  | 'versions'
  | 'builtwith'
  | 'activity';

const STOREFRONTS = [
  { code: 'US', name: 'United States', flag: '🇺🇸', currency: 'USD ($)' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽', currency: 'MXN ($)' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', currency: 'CAD ($)' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', currency: 'GBP (£)' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', currency: 'EUR (€)' },
  { code: 'FR', name: 'France', flag: '🇫🇷', currency: 'EUR (€)' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', currency: 'EUR (€)' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', currency: 'EUR (€)' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', currency: 'BRL (R$)' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', currency: 'JPY (¥)' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', currency: 'KRW (₩)' },
  { code: 'IN', name: 'India', flag: '🇮🇳', currency: 'INR (₹)' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', currency: 'AUD ($)' }
];

export const AppProfileView: React.FC<AppProfileViewProps> = ({
  appId,
  onNavigate,
  onSelectApp,
  onSelectDeveloper,
  onCompare,
  onOpenSignIn
}) => {
  const { t } = useI18n();
  const [app, setApp] = useState<AppData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<ProfileTab>('overview');
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('US');
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isVerifiedOwner, setIsVerifiedOwner] = useState(false);
  const [ownershipData, setOwnershipData] = useState<AppOwnership | null>(null);
  const [verifiedAnalytics, setVerifiedAnalytics] = useState<VerifiedAnalyticsResponse | null>(null);
  const [activeViewMode, setActiveViewMode] = useState<'public' | 'owner_analytics'>('public');
  const [showClaimModal, setShowClaimModal] = useState(false);

  const loadDeveloperOwnership = async (appStoreId: number | string) => {
    try {
      const res = await DeveloperAnalyticsService.checkOwnership(appStoreId);
      if (res.isOwner && res.ownership?.verification_status === 'verified') {
        setIsVerifiedOwner(true);
        setOwnershipData(res.ownership);
        const analytics = await DeveloperAnalyticsService.getVerifiedAnalytics(appStoreId);
        if (analytics && analytics.verified) {
          setVerifiedAnalytics(analytics);
          setActiveViewMode('owner_analytics');
        }
      } else {
        setIsVerifiedOwner(false);
        setOwnershipData(null);
        setVerifiedAnalytics(null);
        setActiveViewMode('public');
      }
    } catch (e) {
      console.warn('Ownership check error:', e);
    }
  };

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    appCatalog
      .getApp(appId)
      .then((fetchedApp) => {
        if (isMounted) {
          if (fetchedApp) {
            setApp(fetchedApp);
            setIsWatchlisted(StorageService.isWatchlisted(fetchedApp.id));
            // Canonical page title update
            document.title = `${fetchedApp.name} App Stats, Rankings & Reviews | AppStatsView`;
            loadDeveloperOwnership(fetchedApp.appStoreId || fetchedApp.id);
          }
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [appId]);

  const handleToggleWatchlist = () => {
    const user = StorageService.getUser();
    if (!user) {
      if (onOpenSignIn) onOpenSignIn();
      return;
    }
    if (!app) return;
    const added = StorageService.toggleWatchlist(app.id);
    setIsWatchlisted(added);
    setToastMessage(added ? `Added ${app.name} to Watchlist!` : `Removed ${app.name} from Watchlist`);
    setTimeout(() => setToastMessage(null), 2500);
  };

  const handleAddToCollection = () => {
    const user = StorageService.getUser();
    if (!user) {
      if (onOpenSignIn) onOpenSignIn();
      return;
    }
    setShowCollectionModal(true);
  };

  const handleNativeShare = () => {
    if (!app) return;
    const canonicalPath = `/app/${app.appStoreId || app.id}/${app.slug}`;
    const fullUrl = `https://appstatsview.com${canonicalPath}`;
    const shareText = `Explore ${app.name} mobile app intelligence on AppStatsView: ratings, versions, growth score and global rankings.`;

    if (navigator.share) {
      navigator
        .share({
          title: `${app.name} — AppStatsView Profile`,
          text: shareText,
          url: fullUrl
        })
        .catch(() => setShowShareModal(true));
    } else {
      setShowShareModal(true);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-24 flex flex-col items-center justify-center space-y-4">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
        <p className="text-sm font-semibold text-slate-600">
          Retrieving global application intelligence & telemetry...
        </p>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 text-center space-y-4">
        <h2 className="text-2xl font-black text-slate-900">Application Not Found</h2>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          The requested application ID could not be located in the catalog database or Apple Public Storefronts.
        </p>
        <button
          onClick={() => onNavigate('/apps')}
          className="px-5 py-2.5 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 cursor-pointer"
        >
          Browse App Catalog
        </button>
      </div>
    );
  }

  // Find country-specific observation for current selected storefront
  const currentStorefrontInfo = STOREFRONTS.find((s) => s.code === selectedCountryCode) || STOREFRONTS[0];
  const countryObservation = app.countryRankings?.find((c) => c.countryCode.toUpperCase() === selectedCountryCode);

  const tabs: { id: ProfileTab; label: string; icon: React.ReactNode; badge?: string }[] = [
    { id: 'overview', label: t('tab.overview'), icon: <Layers className="w-4 h-4" /> },
    { id: 'appstore', label: t('tab.appStore'), icon: <StoreBadge store="appstore" size="xs" /> },
    { id: 'googleplay', label: t('tab.googlePlay'), icon: <StoreBadge store="googleplay" size="xs" />, badge: 'Pending' },
    { id: 'rankings', label: t('tab.rankings'), icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'reviews', label: t('tab.reviews'), icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'keywords', label: t('tab.keywords'), icon: <Key className="w-4 h-4" /> },
    { id: 'countries', label: t('tab.countries'), icon: <Globe className="w-4 h-4" /> },
    { id: 'competitors', label: t('tab.competitors'), icon: <Scale className="w-4 h-4" /> },
    { id: 'versions', label: t('tab.versions'), icon: <History className="w-4 h-4" /> },
    { id: 'builtwith', label: t('tab.builtWith'), icon: <Cpu className="w-4 h-4" /> },
    { id: 'activity', label: t('tab.activity'), icon: <Activity className="w-4 h-4" />, badge: 'Live' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8 animate-fadeIn">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-4 py-3 rounded-2xl shadow-xl border border-slate-700 text-xs font-bold flex items-center gap-2 animate-fadeIn">
          <Check className="w-4 h-4 text-emerald-400" strokeWidth={2.5} />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* 1. TOP STOREFRONT SELECTOR BAR */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white/90 backdrop-blur-md px-5 py-3 rounded-2xl border border-slate-200/80 shadow-2xs">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-blue-600" />
          <span className="text-xs font-bold text-slate-700">{t('profile.storefrontSelector')}:</span>
          <select
            value={selectedCountryCode}
            onChange={(e) => setSelectedCountryCode(e.target.value)}
            aria-label="Global Storefront Country Selector"
            className="px-3 py-1 rounded-xl bg-slate-100/90 border border-slate-200 text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer"
          >
            {STOREFRONTS.map((sf) => (
              <option key={sf.code} value={sf.code}>
                {sf.flag} {sf.name} ({sf.currency})
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1 font-medium">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
            <span>Store Availability: Active ({selectedCountryCode})</span>
          </span>
          <span>•</span>
          <span className="font-mono text-slate-400">Canonical: /app/{app.appStoreId || app.id}/{app.slug}</span>
        </div>
      </div>

      {/* 2. MAIN APP PROFILE HERO HEADER */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-sm relative overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* Icon & Details */}
          <div className="flex items-start gap-4 sm:gap-6">
            <AppIcon
              app={app}
              size="xl"
              shape="squircle"
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl shadow-md border border-slate-100 flex-shrink-0"
            />
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-slate-950 tracking-tight">
                  {app.name}
                </h1>
                <StoreBadge store={app.primaryStore} size="md" />
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <Check className="w-3 h-3 text-emerald-600" strokeWidth={2.5} />
                  <span>{t('profile.appleMetadata')}</span>
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                  <span>{currentStorefrontInfo.flag} {currentStorefrontInfo.name}</span>
                </span>
              </div>

              <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-500 flex-wrap">
                <button
                  onClick={() => onSelectDeveloper(app.developerSlug)}
                  className="font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                >
                  {app.developer}
                </button>
                <span>•</span>
                <span className="font-medium text-slate-700">{app.category}</span>
                <span>•</span>
                <span className="flex items-center gap-1 font-bold text-amber-500">
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" strokeWidth={1.5} />
                  <span>{app.rating.toFixed(1)}</span>
                  <span className="text-slate-400 font-normal">
                    ({app.totalReviews > 1000000 ? `${(app.totalReviews / 1000000).toFixed(1)}M` : `${(app.totalReviews / 1000).toFixed(0)}K`} ratings)
                  </span>
                </span>
                {app.appStoreId && (
                  <>
                    <span>•</span>
                    <span className="text-slate-400 text-xs font-mono">App Store ID: {app.appStoreId}</span>
                  </>
                )}
                {app.bundleId && (
                  <>
                    <span>•</span>
                    <span className="text-slate-400 text-xs font-mono">{app.bundleId}</span>
                  </>
                )}
              </div>

              <p className="text-xs text-slate-600 mt-2 max-w-2xl leading-relaxed">
                {app.subtitle || app.description}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2.5 flex-wrap self-start lg:self-center">
            {/* Developer Claim / Owner Analytics Mode Switcher */}
            {isVerifiedOwner ? (
              <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-2xl border border-slate-200 shadow-2xs">
                <button
                  onClick={() => setActiveViewMode('public')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    activeViewMode === 'public'
                      ? 'bg-white text-slate-900 shadow-xs'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  Public View
                </button>
                <button
                  onClick={() => setActiveViewMode('owner_analytics')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                    activeViewMode === 'owner_analytics'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'text-emerald-700 hover:bg-emerald-50'
                  }`}
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span>Owner Analytics</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowClaimModal(true)}
                className="px-4 py-2.5 rounded-2xl text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white transition-all flex items-center gap-1.5 shadow-sm shadow-emerald-600/20 cursor-pointer"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Claim this App</span>
              </button>
            )}

            {app.appStoreUrl && (
              <a
                href={app.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3.5 py-2.5 rounded-2xl text-xs font-bold bg-slate-900 hover:bg-black text-white transition-all flex items-center gap-1.5 shadow-xs"
              >
                <span>{t('profile.viewOnAppStore')}</span>
                <ExternalLink className="w-3 h-3" strokeWidth={2} />
              </a>
            )}

            <button
              onClick={handleToggleWatchlist}
              className={`px-4 py-2.5 rounded-2xl text-xs font-bold border transition-all flex items-center gap-1.5 shadow-xs cursor-pointer ${
                isWatchlisted
                  ? 'bg-blue-600 text-white border-blue-600 shadow-blue-500/20'
                  : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-200'
              }`}
            >
              <Bookmark className={`w-3.5 h-3.5 ${isWatchlisted ? 'fill-current' : ''}`} strokeWidth={2} />
              <span>{isWatchlisted ? t('nav.inWatchlist') : t('nav.trackApp')}</span>
            </button>

            <button
              onClick={() => onCompare([app.id])}
              className="px-4 py-2.5 rounded-2xl text-xs font-bold bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <Scale className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span>{t('nav.compare')}</span>
            </button>

            <button
              onClick={handleAddToCollection}
              className="px-4 py-2.5 rounded-2xl text-xs font-bold bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <FolderKanban className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span>{t('nav.addToCollection')}</span>
            </button>

            <button
              onClick={handleNativeShare}
              className="px-4 py-2.5 rounded-2xl text-xs font-bold bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              <Share2 className="w-3.5 h-3.5" strokeWidth={2} />
              <span>{t('nav.share')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 3. CONDITIONAL OWNER ANALYTICS DASHBOARD OR PUBLIC TABS */}
      {isVerifiedOwner && activeViewMode === 'owner_analytics' && verifiedAnalytics ? (
        <DeveloperAnalyticsDashboard
          app={app}
          analytics={verifiedAnalytics}
          onRefresh={() => loadDeveloperOwnership(app.appStoreId || app.id)}
        />
      ) : (
        <>
          {/* 3. ESTIMATED & CORE KEY METRICS STRIP WITH DATA TRUST ENGINE PROVENANCE */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {/* Metric 1 - Search Position (Rule 10) */}
            {(() => {
              const m = DataTrustEngine.resolveMetric(app, 'searchPosition', 'public');
              return (
                <div className="bg-white p-4 rounded-3xl border border-slate-200/80 shadow-2xs">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                    {t('profile.searchPosition')}
                  </span>
                  <div className="text-2xl font-black text-slate-900 tracking-tight mt-0.5">
                    #{countryObservation?.rank || app.currentRank}
                  </div>
                  <div className="mt-1">
                    <MetricSourceBadge
                      trustLevel={m.trustLevel}
                      metricData={m}
                      size="xs"
                    />
                  </div>
                </div>
              );
            })()}

            {/* Metric 2 - Category Pos */}
            <div className="bg-white p-4 rounded-3xl border border-slate-200/80 shadow-2xs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                {t('profile.categoryPosition')}
              </span>
              <div className="text-2xl font-black text-slate-900 tracking-tight mt-0.5">
                #{app.categoryRank}
              </div>
              <span className="text-[11px] font-semibold text-slate-500 truncate block mt-1">{app.category}</span>
            </div>

            {/* Metric 3 - Growth Score (Rule 28) */}
            {(() => {
              const m = DataTrustEngine.resolveMetric(app, 'growthScore', 'public');
              return (
                <div className="bg-white p-4 rounded-3xl border border-slate-200/80 shadow-2xs">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                    {t('profile.growthScore')}
                  </span>
                  <div className="text-2xl font-black text-blue-600 tracking-tight mt-0.5">
                    {app.growthScore} <small className="text-xs text-slate-400 font-normal">/100</small>
                  </div>
                  <div className="mt-1">
                    <MetricSourceBadge
                      trustLevel={m.trustLevel}
                      metricData={m}
                      size="xs"
                      customLabel="AppStatsView Score"
                    />
                  </div>
                </div>
              );
            })()}

            {/* Metric 4 - Rating (Rule 3) */}
            {(() => {
              const m = DataTrustEngine.resolveMetric(app, 'rating', 'public');
              return (
                <div className="bg-white p-4 rounded-3xl border border-slate-200/80 shadow-2xs">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                    {t('profile.globalRating')}
                  </span>
                  <div className="text-2xl font-black text-slate-900 tracking-tight mt-0.5 inline-flex items-center gap-1.5">
                    <span>{app.rating.toFixed(1)}</span>
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" strokeWidth={1.5} />
                  </div>
                  <div className="mt-1">
                    <MetricSourceBadge
                      trustLevel={m.trustLevel}
                      metricData={m}
                      size="xs"
                    />
                  </div>
                </div>
              );
            })()}

            {/* Metric 5 - Estimated Downloads (Rule 15, 16, 17) */}
            {(() => {
              const m = DataTrustEngine.resolveMetric(app, 'estimatedMonthlyDownloads', 'public');
              return (
                <div className="bg-white p-4 rounded-3xl border border-blue-100 shadow-2xs bg-blue-50/20">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">
                      {t('profile.estimatedDownloads')}
                    </span>
                  </div>
                  <div className="text-2xl font-black text-slate-900 tracking-tight mt-0.5">
                    {app.estimatedMonthlyDownloads > 1000000
                      ? `${(app.estimatedMonthlyDownloads / 1000000).toFixed(1)}M`
                      : `${(app.estimatedMonthlyDownloads / 1000).toFixed(0)}K`}
                  </div>
                  <div className="mt-1">
                    <MetricSourceBadge
                      trustLevel={m.trustLevel}
                      metricData={m}
                      size="xs"
                    />
                  </div>
                </div>
              );
            })()}

            {/* Metric 6 - Estimated Revenue (Rule 15, 16, 17) */}
            {(() => {
              const m = DataTrustEngine.resolveMetric(app, 'estimatedMonthlyRevenueUsd', 'public');
              return (
                <div className="bg-white p-4 rounded-3xl border border-emerald-100 shadow-2xs bg-emerald-50/20">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">
                      {t('profile.estimatedRevenue')}
                    </span>
                  </div>
                  <div className="text-2xl font-black text-slate-900 tracking-tight mt-0.5">
                    {app.estimatedMonthlyRevenueUsd > 1000000
                      ? `$${(app.estimatedMonthlyRevenueUsd / 1000000).toFixed(1)}M`
                      : `$${(app.estimatedMonthlyRevenueUsd / 1000).toFixed(0)}K`}
                  </div>
                  <div className="mt-1">
                    <MetricSourceBadge
                      trustLevel={m.trustLevel}
                      metricData={m}
                      size="xs"
                    />
                  </div>
                </div>
              );
            })()}
          </div>

      {/* 4. TABS NAVIGATION BAR: Mobile Horizontal Scrollable Pills & Desktop Sidebar Structure */}
      {/* Mobile Horizontal Tabs Bar */}
      <div className="lg:hidden w-full overflow-x-auto pb-2 -mx-4 px-4 scrollbar-none flex items-center gap-2">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                isActive
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200/80 shadow-2xs'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.badge && (
                <span
                  className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : tab.badge === 'Live'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Sidebar on Desktop (Navigation & Summary Profile) */}
        <aside className="hidden lg:block lg:col-span-3 space-y-4">
          <div className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-xs space-y-4 sticky top-24">
            {/* Quick Entity Card in Sidebar */}
            <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
              <AppIcon app={app} size="sm" shape="squircle" className="w-12 h-12 rounded-2xl flex-shrink-0" />
              <div className="min-w-0">
                <h4 className="text-xs font-black text-slate-900 truncate">{app.name}</h4>
                <p className="text-[11px] text-slate-500 truncate">{app.developer}</p>
                <span className="text-[10px] font-semibold text-blue-600">{app.category}</span>
              </div>
            </div>

            {/* Vertical Navigation Tabs */}
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-2xl text-xs font-bold transition-all cursor-pointer ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      {tab.icon}
                      <span>{tab.label}</span>
                    </div>
                    {tab.badge && (
                      <span
                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                          isActive
                            ? 'bg-white/20 text-white'
                            : tab.badge === 'Live'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Store Quick Links */}
            <div className="pt-3 border-t border-slate-100 space-y-2">
              {app.appStoreUrl && (
                <a
                  href={app.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-between px-3.5 py-2 rounded-xl text-xs font-semibold bg-slate-50 hover:bg-slate-100 text-slate-700 transition-all"
                >
                  <span>{t('profile.viewOnAppStore')}</span>
                  <ExternalLink className="w-3 h-3 text-slate-400" />
                </a>
              )}
              <div className="px-3.5 py-2 rounded-xl text-xs font-semibold bg-slate-50/50 text-slate-400 flex items-center justify-between">
                <span>{t('profile.viewOnGooglePlay')}</span>
                <span className="text-[10px] font-mono">Pending</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Entity Tab Content Area */}
        <main className="lg:col-span-9 space-y-8">
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Outlier Alert if present */}
              {app.outlier?.isOutlier && <OutlierBadge outlier={app.outlier} />}

              {/* Chart & Growth Gauge */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-7">
                  <RankingChart rankingsHistory={app.rankingsHistory} />
                </div>
                <div className="lg:col-span-5">
                  <GrowthScoreGauge score={app.growthScore} factors={app.growthFactors} />
                </div>
              </div>

              {/* Country Rankings & AI Insights */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-7">
                  <CountryRankings rankings={app.countryRankings} />
                </div>
                <div className="lg:col-span-5">
                  <AIInsightsCard insights={app.aiInsights} title={`${app.name.split(':')[0]} AI Analysis`} />
                </div>
              </div>

              {/* Keyword Preview */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-slate-900">Trending Keyword Visibility</h3>
                  <button
                    onClick={() => setActiveTab('keywords')}
                    className="text-xs font-bold text-blue-600 hover:underline cursor-pointer"
                  >
                    View all keywords →
                  </button>
                </div>
                <KeywordTable keywords={app.keywords} />
              </div>

              {/* Competitor Snapshot */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-slate-900">Competitor Snapshot</h3>
                  <button
                    onClick={() => setActiveTab('competitors')}
                    className="text-xs font-bold text-blue-600 hover:underline cursor-pointer"
                  >
                    Explore competitors →
                  </button>
                </div>
                <CompetitorTable
                  competitorIds={app.competitorIds}
                  currentAppId={app.id}
                  onCompare={onCompare}
                  onSelectApp={onSelectApp}
                />
              </div>

              {/* Built With Summary */}
              <div className="p-6 bg-white rounded-3xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Cpu className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Public Build Intelligence Available</h4>
                    <p className="text-xs text-slate-500">
                      Framework, backend architecture, and verified engineering telemetry available for this title.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setActiveTab('builtwith')}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer self-start sm:self-center"
                >
                  View Build Details
                </button>
              </div>
            </div>
          )}

          {/* TAB 2: APP STORE */}
          {activeTab === 'appstore' && <AppStoreTab app={app} />}

          {/* TAB 3: GOOGLE PLAY */}
          {activeTab === 'googleplay' && <GooglePlayTab app={app} />}

          {/* TAB 4: RANKINGS */}
          {activeTab === 'rankings' && (
            <div className="space-y-6">
              <RankingChart rankingsHistory={app.rankingsHistory} />
              <ProTeaserBanner
                title={t('pro.unlockChart')}
                subtitle="Access 90-day, 1-year, and all-time Search Position and category trend tracking across 130+ App Store countries."
                onUpgrade={() => onNavigate('/pricing')}
              />
              <CountryRankings rankings={app.countryRankings} />
            </div>
          )}

          {/* TAB 5: REVIEWS */}
          {activeTab === 'reviews' && <ReviewsTab app={app} />}

          {/* TAB 6: KEYWORDS */}
          {activeTab === 'keywords' && (
            <div className="space-y-6">
              <KeywordTable keywords={app.keywords} />
              <ProTeaserBanner
                title={t('pro.unlockKeywords')}
                subtitle="Track organic keywords, search difficulty, search volume index, and competitor share of voice with AppStatsView Pro."
                onUpgrade={() => onNavigate('/pricing')}
              />
            </div>
          )}

          {/* TAB 7: COUNTRIES */}
          {activeTab === 'countries' && (
            <div className="space-y-6">
              <CountryRankings rankings={app.countryRankings} />
            </div>
          )}

          {/* TAB 8: COMPETITORS */}
          {activeTab === 'competitors' && (
            <div className="space-y-6">
              <CompetitorTable
                competitorIds={app.competitorIds}
                currentAppId={app.id}
                onCompare={onCompare}
                onSelectApp={onSelectApp}
              />
              <ProTeaserBanner
                title={t('pro.unlockCompetitors')}
                subtitle="Discover new insurgent competitors, track crossover audience overlap, and receive instant alerts when a rival releases a major update."
                onUpgrade={() => onNavigate('/pricing')}
              />
            </div>
          )}

          {/* TAB 9: VERSIONS */}
          {activeTab === 'versions' && (
            <div className="space-y-6">
              <VersionHistorySection versions={app.versions} averageUpdateDays={app.averageUpdateDays} />
            </div>
          )}

          {/* TAB 10: BUILT WITH */}
          {activeTab === 'builtwith' && <BuiltWithTab app={app} />}

          {/* TAB 11: ACTIVITY */}
          {activeTab === 'activity' && <ActivityTab app={app} />}
        </main>
      </div>
    </>
  )}

  {/* Claim App Modal */}
  <ClaimAppModal
    isOpen={showClaimModal}
    onClose={() => setShowClaimModal(false)}
    app={app}
    onSuccess={() => {
      setToastMessage('App Store Connect account connected & verified!');
      loadDeveloperOwnership(app.appStoreId || app.id);
      setTimeout(() => setToastMessage(null), 3000);
    }}
  />

  {/* Share Modal */}
  <ShareModal
    isOpen={showShareModal}
    onClose={() => setShowShareModal(false)}
    title={app.name}
    url={`/app/${app.appStoreId || app.id}/${app.slug}`}
    description={`Explore ${app.name} mobile app intelligence on AppStatsView: ratings, versions, growth score and global rankings.`}
  />

      {/* Add To Collection Modal */}
      <AddToCollectionModal
        appId={app.id}
        appName={app.name}
        isOpen={showCollectionModal}
        onClose={() => setShowCollectionModal(false)}
      />
    </div>
  );
};
